import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import * as moment from 'moment';

declare var $: any;

/**
 * @component DateRangePopupComponent
 * @description A popup/modal component for selecting a date range. Users can select start and end dates
 * using a date picker interface. Supports date validation, default values, and emits selected date range
 * to parent component. Uses moment.js for date manipulation and jQuery for DOM interactions.
 * 
 * @selector app-date-range-popup
 * @template ./date-range-popup.component.html
 * @styleUrl ./date-range-popup.component.scss
 */
@Component({
  selector: 'app-date-range-popup',
  templateUrl: './date-range-popup.component.html',
  styleUrls: ['./date-range-popup.component.scss']
})
export class DateRangePopupComponent {

  /**
   * @property {Date} maxDate
   * @description Maximum selectable date limit. Set to today's date to prevent future date selection.
   */
  maxDate = new Date();

  /**
   * @property {FormGroup} dateRangeForm
   * @description FormGroup managing start and end date form controls. Contains the date picker fields
   * for selecting the date range with reactive form validation support.
   */
  dateRangeForm!: FormGroup;

  /**
   * @type {Object} selected
   * @property {moment.Moment} startDate - The selected start date as a moment object
   * @property {moment.Moment} endDate - The selected end date as a moment object
   * @description Object storing the currently selected date range in moment.js format.
   */
  selected: {startDate: moment.Moment, endDate: moment.Moment};

  /**
   * @property {string} fromDate
   * @description Input property receiving the selected 'fromDate' from parent component.
   * Format: 'DD-MMM-YYYY'. Provides the initial start date for the date range picker.
   */
  @Input() fromDate: string = ''; // Selected 'fromDate' passed from the parent

  /**
   * @property {string} toDate
   * @description Input property receiving the selected 'toDate' from parent component.
   * Format: 'DD-MMM-YYYY'. Provides the initial end date for the date range picker.
   */
  @Input() toDate: string = ''; // Selected 'toDate' passed from the parent

  /**
   * @property {EventEmitter<void>} close
   * @description Output event emitted when user closes the date range popup without applying changes.
   * Parent component should handle this event to hide/dismiss the popup.
   */
  @Output() close = new EventEmitter<void>();

  /**
   * @property {EventEmitter} apply
   * @description Output event emitted with selected date range when user applies the selection.
   * Emits an object containing formatted fromDate and toDate strings (DD-MMM-YYYY format).
   */
  @Output() apply = new EventEmitter<{ fromDate: string; toDate: string }>();

  /**
   * @method constructor
   * @description Initializes the component with the FormBuilder service required for creating
   * and managing the date range form group.
   * 
   * @param {FormBuilder} formBuilder - Angular FormBuilder service for reactive form construction
   */
  constructor(public formBuilder: FormBuilder) {

  }

  /**
   * @method ngOnInit
   * @description Angular lifecycle hook called when the component is initialized.
   * Initializes the date picker, sets default values for fromDate and toDate if not provided,
   * and synchronizes form values with the selected date range.
   * 
   * @returns {void}
   */
  public ngOnInit() {
    this.initializeDatePicker();

    // Set defaults if no input values
    if (!this.fromDate) {
      this.fromDate = moment().format('DD-MMM-YYYY');
    }

    if (!this.toDate) {
      this.toDate = moment().format('DD-MMM-YYYY');
    }

    // Set jQuery field values
    $('#fromDate').val(this.fromDate);
    $('#toDate').val(this.toDate);

    // Parse to actual date for the Angular form
    const fromDateMoment = moment(this.fromDate, 'DD-MMM-YYYY');
    const toDateMoment = moment(this.toDate, 'DD-MMM-YYYY');

    if (fromDateMoment.isValid() && toDateMoment.isValid()) {
      this.dateRangeForm.patchValue({
        start: fromDateMoment.toDate(),
        end: toDateMoment.toDate()
      });

      // Also update internal selected range
      this.selected = {
        startDate: fromDateMoment,
        endDate: toDateMoment
      };
    }
  }


  /**
    * @method initializeDatePicker
    * @description Initializes the date range form group with start and end date controls.
    * Sets up value change subscriptions to track form changes. Prepares the form for date range
    * selection with reactive form validation support.
    * 
    * @returns {void}
    */
  public initializeDatePicker(): void {
    this.dateRangeForm = this.formBuilder.group({
      start: [null],
      end: [null]
    });

    this.dateRangeForm.valueChanges.subscribe(val => {
      const { start, end } = val;
      if (!start || !end) {
      }
    });
  }

  /**
   * @method onPickerClosed
   * @description Handles the event when the date picker is closed. If only start date is selected,
   * sets the end date equal to start date. If both dates are selected, updates the internal selected
   * range object with the chosen dates.
   * 
   * @returns {void}
   */
  public onPickerClosed(): void {
    const { start, end } = this.dateRangeForm.value;

    if (start && !end) {
      // Only start selected → default end = start
      this.dateRangeForm.patchValue({ end: start }, { emitEvent: false });
      this.onDateChanged({ startDate: start, endDate: start });
    } else if (start && end) {
      this.onDateChanged({ startDate: start, endDate: end });
    }
  }

  /**
   * @method onDateChanged
   * @description Updates the internal selected date range when dates are changed in the picker.
   * Converts Date objects to moment.js objects for consistent date manipulation and storage.
   * 
   * @param {Object} range - Object containing the selected date range
   * @param {Date} range.startDate - The selected start date as a Date object
   * @param {Date} range.endDate - The selected end date as a Date object
   * @returns {void}
   */
  public onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
  }

  /**
   * @method closePopup
   * @description Emits the close event to notify parent component to dismiss/hide the date range popup.
   * Called when user clicks the close button without applying date selection.
   * 
   * @returns {void}
   */
  closePopup() {
    this.close.emit();
  }

  /**
   * @method applyDateRange
   * @description Applies the selected date range and emits the result to parent component.
   * Formats the selected dates from moment.js objects to 'DD-MMM-YYYY' string format and emits
   * them via the apply EventEmitter. Also triggers the closePopup method to dismiss the popup.
   * 
   * @returns {void}
   */
  applyDateRange() {
    this.fromDate =  moment(this.selected.startDate).format('DD-MMM-YYYY');
    this.toDate = moment(this.selected.endDate).format('DD-MMM-YYYY');
    this.apply.emit({ fromDate: this.fromDate, toDate: this.toDate });
    this.closePopup();
  }
}
